import { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import {
  FaAngleLeft,
  FaAngleRight,
  FaEdit,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { PAGE_SIZE } from "../Utils/constant";
import TextInput from "../common/TextInput";
import { supabase } from "../../supabaseClient";

const VehicleListNew = () => {
  const [page, setPage] = useState(1);
  const [isEdit, setIsEdit] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [search, setSearch] = useState(null);
  const [vehiclesData, setVehiclesData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [managers, setManagers] = useState([]);

  // Toggle states for collapsible sections
  const [showVehicleLogs, setShowVehicleLogs] = useState(false);
  const [showVehicleCities, setShowVehicleCities] = useState(false);
  const [showScheduleDetails, setShowScheduleDetails] = useState(false);

  // Data for collapsible sections
  const [vehicleLogs, setVehicleLogs] = useState([]);
  const [vehicleCities, setVehicleCities] = useState([]);
  const [scheduleDetails, setScheduleDetails] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      CompanyID: "",
      VehicleStatusID: "",
      Code: "",
      Make: "",
      Model: "",
      PlateNumber: "",
      VIN: "",
      TollTag: "",
      TrackingCode: "",
      ManagerID: "",
    },
  });

  useEffect(() => {
    if (vehicle) {
      reset({
        CompanyID: vehicle.CompanyID,
        VehicleStatusID: vehicle.VehicleStatusID,
        Code: vehicle.Code,
        Make: vehicle.Make,
        Model: vehicle.Model,
        PlateNumber: vehicle.PlateNumber,
        VIN: vehicle.VIN || "",
        TollTag: vehicle.TollTag || "",
        TrackingCode: vehicle.TrackingCode || "",
        ManagerID: vehicle.ManagerID || "",
      });
    }
  }, [vehicle, reset]);

  useEffect(() => {
    const fetchLookups = async () => {
      const { data: Companies } = await supabase
        .from("Companies")
        .select("CompanyID, Name");
      setCompanies(Companies || []);
      const { data: Statuses } = await supabase
        .from("VehicleStatuses")
        .select("VehicleStatusID, Description");
      setStatuses(Statuses || []);
      // Fetch managers (Users with RoleID = 2)
      const { data: Managers } = await supabase
        .from("Users")
        .select("UserID, FirstName, LastName, Email")
        .eq("RoleID", 2)
        .eq("Active", true);
      setManagers(Managers || []);
    };
    fetchLookups();
  }, []);

  useEffect(() => {
    const fetchVehicles = async () => {
      let query = supabase
        .from("Vehicles")
        .select(
          "VehicleID, CompanyID, VehicleStatusID, Code, Make, Model, PlateNumber, VIN, TollTag, TrackingCode, ManagerID",
          { count: "exact", head: false }
        )
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);
      if (search && search.text) {
        query = query.ilike(search.type, `%${search.text}%`);
      }
      const { data, count, error } = await query;
      if (!error) {
        setVehiclesData(data);
        setTotalCount(count);
      }
    };
    fetchVehicles();
  }, [page, search, isEdit]);

  const onNextPage = useCallback(() => {
    setPage((old) => old + 1);
  }, []);
  const onPrevPage = useCallback(() => {
    setPage((old) => Math.max(old - 1, 1));
  }, []);

  const onSuccess = useCallback(() => {
    toast.success("Success");
    setIsEdit(false);
    setVehicle(null);
  }, []);
  const onError = useCallback((err) => {
    toast.error("Error: " + err.message);
  }, []);

  const onAddNew = () => {
    reset({
      CompanyID: companies[0] ? companies[0].CompanyID : "",
      VehicleStatusID: statuses[0] ? statuses[0].VehicleStatusID : "",
      Code: "",
      Make: "",
      Model: "",
      PlateNumber: "",
      VIN: "",
      TollTag: "",
      TrackingCode: "",
      ManagerID: managers[0] ? managers[0].UserID : "",
    });
    setIsEdit(true);
    setVehicle(null);
  };

  const OnCancel = () => {
    setIsEdit(false);
    setVehicle(null);
  };

  const OnCreate = useCallback(
    async (data) => {
      console.log("___________", data, vehicle);
      if (vehicle) {
        // Update
        const { error } = await supabase
          .from("Vehicles")
          .update({
            CompanyID: data.CompanyID,
            VehicleStatusID: data.VehicleStatusID,
            Code: data.Code,
            Make: data.Make,
            Model: data.Model,
            PlateNumber: data.PlateNumber,
            VIN: data.VIN,
            TollTag: data.TollTag,
            TrackingCode: data.TrackingCode,
            ManagerID: data.ManagerID,
          })
          .eq("VehicleID", vehicle.VehicleID);
        if (error) onError(error);
        else onSuccess();
      } else {
        // Create
        const { error } = await supabase.from("Vehicles").insert({
          CompanyID: data.CompanyID,
          VehicleStatusID: data.VehicleStatusID,
          Code: data.Code,
          Make: data.Make,
          Model: data.Model,
          PlateNumber: data.PlateNumber,
          VIN: data.VIN,
          TollTag: data.TollTag,
          TrackingCode: data.TrackingCode,
          ManagerID: data.ManagerID,
        });
        if (error) onError(error);
        else onSuccess();
      }
    },
    [vehicle, onError, onSuccess]
  );

  const OnSearch = useCallback((data) => {
    setSearch(data);
  }, []);

  // Fetch Vehicle Logs
  const fetchVehicleLogs = useCallback(async (vehicleId) => {
    const { data, error } = await supabase
      .from("VehicleLogs")
      .select(
        `
        VehicleLogID,
        LogDate,
        Mileage,
        VehicleLogTypeID (Code, Description),
        UserID
      `
      )
      .eq("VehicleID", vehicleId)
      .order("LogDate", { ascending: false });

    if (!error) {
      setVehicleLogs(data || []);
    } else {
      console.error("Error fetching vehicle logs:", error);
      setVehicleLogs([]);
    }
  }, []);

  // Fetch Vehicle Cities (via CityID relationship)
  const fetchVehicleCities = useCallback(async (vehicleId) => {
    const { data: vehicleData, error } = await supabase
      .from("Vehicles")
      .select(
        `
        CityID,
        Cities (CityID, Code, Description)
      `
      )
      .eq("VehicleID", vehicleId)
      .single();

    if (!error && vehicleData?.Cities) {
      setVehicleCities([vehicleData.Cities]);
    } else {
      console.error("Error fetching vehicle cities:", error);
      setVehicleCities([]);
    }
  }, []);

  // Fetch Schedule Details
  const fetchScheduleDetails = useCallback(async (vehicleId) => {
    const { data, error } = await supabase
      .from("Schedule")
      .select(
        `
        ScheduleID,
        ScheduleDate,
        FlightNo,
        ScheduleStatusID (Code, Description),
        AirlineID (Code, Description),
        DriverID,
        TransportationTypeID (Code, Description)
      `
      )
      .eq("VehicleID", vehicleId)
      .order("ScheduleDate", { ascending: false })
      .limit(10);

    if (!error) {
      setScheduleDetails(data || []);
    } else {
      console.error("Error fetching schedule details:", error);
      setScheduleDetails([]);
    }
  }, []);

  // Toggle handlers
  const handleToggleVehicleLogs = useCallback(() => {
    if (!showVehicleLogs && vehicle) {
      fetchVehicleLogs(vehicle.VehicleID);
    }
    setShowVehicleLogs(!showVehicleLogs);
  }, [showVehicleLogs, vehicle, fetchVehicleLogs]);

  const handleToggleVehicleCities = useCallback(() => {
    if (!showVehicleCities && vehicle) {
      fetchVehicleCities(vehicle.VehicleID);
    }
    setShowVehicleCities(!showVehicleCities);
  }, [showVehicleCities, vehicle, fetchVehicleCities]);

  const handleToggleScheduleDetails = useCallback(() => {
    if (!showScheduleDetails && vehicle) {
      fetchScheduleDetails(vehicle.VehicleID);
    }
    setShowScheduleDetails(!showScheduleDetails);
  }, [showScheduleDetails, vehicle, fetchScheduleDetails]);

  return (
    <>
      {isEdit ? (
        <div className="driver-patent-edit-content mt-4 rounded-lg">
          <div className="flex justify-between mb-2">
            <div>Company</div>
            <div className="w-35">
              <Controller
                control={control}
                name="CompanyID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.CompanyID?.message}
                  >
                    {companies.map((company) => (
                      <option key={company.CompanyID} value={company.CompanyID}>
                        {company.Name}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Status</div>
            <div className="w-35">
              <Controller
                control={control}
                name="VehicleStatusID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.VehicleStatusID?.message}
                  >
                    {statuses.map((status) => (
                      <option
                        key={status.VehicleStatusID}
                        value={status.VehicleStatusID}
                      >
                        {status.Description}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Code</div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="Code"
                rules={{ required: "Code is required" }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.Code?.message}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Make</div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="Make"
                rules={{ required: "Make is required" }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.Make?.message}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Model</div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="Model"
                rules={{ required: "Model is required" }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.Model?.message}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Plate Number</div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="PlateNumber"
                rules={{ required: "Plate Number is required" }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.PlateNumber?.message}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>VIN</div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="VIN"
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Toll Tag</div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="TollTag"
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Manager</div>
            <div className="w-35">
              <Controller
                control={control}
                name="ManagerID"
                rules={{ required: "Manager is required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.ManagerID?.message}
                  >
                    {managers.map((manager) => (
                      <option key={manager.UserID} value={manager.UserID}>
                        {manager.FirstName} {manager.LastName}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Tracking Code</div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="TrackingCode"
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                  />
                )}
              />
            </div>
          </div>
          <div className="flex gap-4 mt-4 save-button">
            <button
              disabled={false}
              onClick={handleSubmit(OnCreate)}
              className="justify-between p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
            >
              <span>{vehicle ? "Update" : "Create"}</span>
            </button>
            <div>
              <div
                onClick={OnCancel}
                className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
              >
                <span>Cancel</span>
              </div>
            </div>
          </div>

          {/* Collapsible sections - only show in edit mode */}
          {vehicle && (
            <div className="mt-6">
              {/* Vehicle Log Details */}
              <div className="mb-4">
                <div
                  onClick={handleToggleVehicleLogs}
                  className="flex justify-between items-center cursor-pointer bg-gray-100 p-3 rounded-lg hover:bg-gray-200"
                >
                  <span className="">Vehicle Log Details</span>
                  {showVehicleLogs ? <FaChevronUp /> : <FaChevronDown />}
                </div>
                {showVehicleLogs && (
                  <div className="mt-2 p-3 border border-gray-200 rounded-lg">
                    {vehicleLogs.length > 0 ? (
                      <table className="table-auto w-full">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-4 py-2 border text-left">
                              Log ID
                            </th>
                            <th className="px-4 py-2 border text-left">
                              Log Date
                            </th>
                            <th className="px-4 py-2 border text-left">Type</th>
                            <th className="px-4 py-2 border text-left">
                              Mileage
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {vehicleLogs.map((log) => (
                            <tr
                              key={log.VehicleLogID}
                              className="hover:bg-gray-50"
                            >
                              <td className="px-4 py-2 border">
                                {log.VehicleLogID}
                              </td>
                              <td className="px-4 py-2 border">
                                {log.LogDate
                                  ? new Date(log.LogDate).toLocaleDateString()
                                  : ""}
                              </td>
                              <td className="px-4 py-2 border">
                                {log.VehicleLogTypeID?.Description || ""}
                              </td>
                              <td className="px-4 py-2 border">
                                {log.Mileage || ""}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-gray-500 text-center py-4">
                        No vehicle logs found
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Vehicle Cities Details */}
              <div className="mb-4">
                <div
                  onClick={handleToggleVehicleCities}
                  className="flex justify-between items-center cursor-pointer bg-gray-100 p-3 rounded-lg hover:bg-gray-200"
                >
                  <span className="">Vehicle Cities Details</span>
                  {showVehicleCities ? <FaChevronUp /> : <FaChevronDown />}
                </div>
                {showVehicleCities && (
                  <div className="mt-2 p-3 border border-gray-200 rounded-lg">
                    {vehicleCities.length > 0 ? (
                      <table className="table-auto w-full">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-4 py-2 border text-left">
                              City ID
                            </th>
                            <th className="px-4 py-2 border text-left">Code</th>
                            <th className="px-4 py-2 border text-left">
                              Description
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {vehicleCities.map((city) => (
                            <tr key={city.CityID} className="hover:bg-gray-50">
                              <td className="px-4 py-2 border">
                                {city.CityID}
                              </td>
                              <td className="px-4 py-2 border">
                                {city.Code || ""}
                              </td>
                              <td className="px-4 py-2 border">
                                {city.Description || ""}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-gray-500 text-center py-4">
                        No city information found
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Schedule Details */}
              <div className="mb-4">
                <div
                  onClick={handleToggleScheduleDetails}
                  className="flex justify-between items-center cursor-pointer bg-gray-100 p-3 rounded-lg hover:bg-gray-200"
                >
                  <span className="">Schedule Details (Last 10)</span>
                  {showScheduleDetails ? <FaChevronUp /> : <FaChevronDown />}
                </div>
                {showScheduleDetails && (
                  <div className="mt-2 p-3 border border-gray-200 rounded-lg">
                    {scheduleDetails.length > 0 ? (
                      <table className="table-auto w-full">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-4 py-2 border text-left">
                              Schedule ID
                            </th>
                            <th className="px-4 py-2 border text-left">Date</th>
                            <th className="px-4 py-2 border text-left">
                              Flight No
                            </th>
                            <th className="px-4 py-2 border text-left">
                              Airline
                            </th>
                            <th className="px-4 py-2 border text-left">
                              Status
                            </th>
                            <th className="px-4 py-2 border text-left">
                              Transport
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {scheduleDetails.map((schedule) => (
                            <tr
                              key={schedule.ScheduleID}
                              className="hover:bg-gray-50"
                            >
                              <td className="px-4 py-2 border">
                                {schedule.ScheduleID}
                              </td>
                              <td className="px-4 py-2 border">
                                {schedule.ScheduleDate
                                  ? new Date(
                                      schedule.ScheduleDate
                                    ).toLocaleDateString()
                                  : ""}
                              </td>
                              <td className="px-4 py-2 border">
                                {schedule.FlightNo || ""}
                              </td>
                              <td className="px-4 py-2 border">
                                {schedule.AirlineID?.Description || ""}
                              </td>
                              <td className="px-4 py-2 border">
                                {schedule.ScheduleStatusID?.Description || ""}
                              </td>
                              <td className="px-4 py-2 border">
                                {schedule.TransportationTypeID?.Description ||
                                  ""}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-gray-500 text-center py-4">
                        No schedule details found
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="flex ">
            <h5 className="mb-0">Vehicle List</h5>
            <button
              className="justify-between rounded-lg ml-1 flex items-center custom-blue-button border-none"
              onClick={onAddNew}
            >
              <span>Add New</span>
            </button>
          </div>
          <SearchBar setSearch={setSearch} OnSearch={OnSearch} />
          <table className="table-auto w-full mt-4 border border-gray-200">
            <thead>
              <tr className="bg-gray-100 cursor-pointer">
                <th className="px-4 py-2 border">ID</th>
                <th className="px-4 py-2 border">Company</th>
                <th className="px-4 py-2 border">Status</th>
                <th className="px-4 py-2 border">Code</th>
                <th className="px-4 py-2 border">Make</th>
                <th className="px-4 py-2 border">Model</th>
                <th className="px-4 py-2 border">Plate Number</th>
                <th className="px-4 py-2 border">VIN</th>
                <th className="px-4 py-2 border">Toll Tag</th>
                <th className="px-4 py-2 border">Tracking Code</th>
                <th className="px-4 py-2 border">Manager</th>
                <th className="px-4 py-2 border">Edit</th>
              </tr>
            </thead>
            <tbody>
              {vehiclesData &&
                vehiclesData.map((vehicle) => (
                  <tr key={vehicle.VehicleID} className="hover:bg-gray-50">
                    <td className="px-4 py-2 border">{vehicle.VehicleID}</td>
                    <td className="px-4 py-2 border">
                      {companies.find((c) => c.CompanyID === vehicle.CompanyID)
                        ?.Name || ""}
                    </td>
                    <td className="px-4 py-2 border">
                      {statuses.find(
                        (s) => s.VehicleStatusID === vehicle.VehicleStatusID
                      )?.Description || ""}
                    </td>
                    <td className="px-4 py-2 border">{vehicle.Code}</td>
                    <td className="px-4 py-2 border">{vehicle.Make}</td>
                    <td className="px-4 py-2 border">{vehicle.Model}</td>
                    <td className="px-4 py-2 border">{vehicle.PlateNumber}</td>
                    <td className="px-4 py-2 border">{vehicle.VIN || ""}</td>
                    <td className="px-4 py-2 border">
                      {vehicle.TollTag || ""}
                    </td>
                    <td className="px-4 py-2 border">
                      {vehicle.TrackingCode || ""}
                    </td>
                    <td className="px-4 py-2 border">
                      {managers.find((m) => m.UserID === vehicle.ManagerID)
                        ? `${managers.find((m) => m.UserID === vehicle.ManagerID).FirstName} ${managers.find((m) => m.UserID === vehicle.ManagerID).LastName}`
                        : "Not Assigned"}
                    </td>
                    <td className="px-4 py-2 border cursor-point">
                      <FaEdit
                        onClick={() => {
                          setVehicle(vehicle);
                          setIsEdit(true);
                        }}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <div className="pagination-container">
            <div className="pagination-content">
              <div>
                <p className="pagination-text">
                  Showing <span>{(page - 1) * PAGE_SIZE + 1}</span> to{" "}
                  <span>{Math.min(totalCount, page * PAGE_SIZE)}</span> of{" "}
                  <span>{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="pagination-nav">
                  <button
                    onClick={onPrevPage}
                    disabled={page === 1}
                    className={`pagination-item ${
                      page === 1 ? "disabled" : ""
                    }`}
                  >
                    <FaAngleLeft className="pagination-icon" />
                  </button>
                  <button
                    onClick={onNextPage}
                    disabled={page * PAGE_SIZE > totalCount}
                    className={`pagination-item ${
                      page * PAGE_SIZE > totalCount ? "disabled" : ""
                    }`}
                  >
                    <FaAngleRight className="pagination-icon" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VehicleListNew;

const SearchBar = ({ setSearch, OnSearch }) => {
  const {
    control: searchControl,
    handleSubmit: handleSearchSubmit,
    reset: searchReset,
  } = useForm({
    defaultValues: {
      type: "Code",
      text: "",
    },
  });

  return (
    <div className="flex mt-4 items-center">
      <div>
        <p className="mb-0">Look For:</p>
      </div>
      <div className="flex justify-between w-10 ml-1">
        <Controller
          control={searchControl}
          name="type"
          render={({ field: { value, onChange } }) => (
            <select
              className="custom-input rounded-lg cursor-pointer"
              value={value}
              onChange={onChange}
            >
              <option value={"Code"}>Code</option>
              <option value={"Make"}>Make</option>
              <option value={"Model"}>Model</option>
              <option value={"PlateNumber"}>Plate Number</option>
            </select>
          )}
        />
      </div>
      <div className="ml-1">
        <Controller
          control={searchControl}
          name="text"
          rules={{ required: "Required" }}
          render={({ field: { value, onChange } }) => (
            <input
              type="text"
              value={value}
              onChange={onChange}
              className="custom-input rounded-lg w-100 "
            />
          )}
        />
      </div>
      <div className="ml-1 flex">
        <div>
          <button
            onClick={handleSearchSubmit(OnSearch)}
            className="justify-between rounded-lg flex items-center w-30 custom-search-button border-none"
          >
            <span>Search</span>
          </button>
        </div>
        <div>
          <button
            onClick={() => {
              searchReset({ type: "Code", text: "" });
              setSearch(null);
            }}
            className="justify-between rounded-lg ml-1 flex items-center custom-reset-button"
          >
            <span>Reset</span>
          </button>
        </div>
      </div>
    </div>
  );
};
